import type { CenterProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Center,
  Icon,
  Motion,
  motion,
  system,
  transition,
} from '@hover/blueprint';
import React from 'react';
import { useLocation } from 'react-router';

import type { ToolbarItemType } from './Toolbar';

const commonDotProps = {
  borderRadius: 'round',
  height: '8px',
  width: '8px',
  backgroundColor: 'transparent',
};

const MotionDot = motion(system.div);

interface ToolbarButtonProps extends CenterProps {
  toolbarItem: ToolbarItemType;
  onClick: () => void;
}

const ToolbarButton = ({
  toolbarItem,
  onClick,
  ...props
}: ToolbarButtonProps) => {
  const { icon, name, mobileOrder, desktopOrder, path } = toolbarItem;
  const { pathname } = useLocation();
  const isSelected = pathname === path;

  return (
    <Center
      alignItems="center"
      as="button"
      bg="transparent"
      border="none"
      cursor="pointer"
      data-group
      flexDirection="column"
      justifyContent="center"
      marginX={200}
      onClick={onClick}
      order={{ base: mobileOrder, tablet: desktopOrder }}
      {...props}
    >
      <Icon
        _groupHover={{ color: isSelected ? 'neutral.900' : 'neutral.700' }}
        color={isSelected ? 'neutral.900' : 'neutral.500'}
        display={{ base: 'block', tablet: 'none' }}
        fill={isSelected ? 'neutral.900' : 'transparent'}
        icon={icon}
        size="large"
        {...transition.easing250('fill', 'color')}
      />
      <Body
        display={{ base: 'none', tablet: 'block' }}
        textTransform="capitalize"
      >
        {name}
      </Body>
      <Box display={{ base: 'none', tablet: 'block' }}>
        <Motion.Tight>
          {isSelected ? (
            <MotionDot
              initial={false}
              {...commonDotProps}
              backgroundColor="currentColor"
              layoutId="dot"
            />
          ) : (
            <system.div {...commonDotProps} />
          )}
        </Motion.Tight>
      </Box>
    </Center>
  );
};

export { ToolbarButton };
